// src/features/users/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { InputField, SelectField } from "@/features/FormCard/Fields";

import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import { adjustDataForSubmission } from "@/components/Form/CountryPhone/data";

import {
  categoryOptions,
  roleOptions,
  statusOptions,
  typeOptions,
} from "./filter";

/* Constants */
const client = "admin-quantilia";
const table_name = "users";

const FormSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email"),
  firstname: z.string().nullable().optional(),
  lastname: z.string().nullable().optional(),

  group_user_name: z.string().nullable().optional(),
  group_user_id: z.string().uuid().optional().nullable(),

  tableau_username: z.string().nullable().optional(),
  category: z.coerce.string().nullable().optional(),
  status: z.coerce.string().nullable().optional(),
  role: z.coerce.string().nullable().optional(),
  type: z.coerce.string().nullable().optional(),
});

export function UsersNewCard() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const navigateToList = () => {
    navigate(-1);
  };

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    disable_translations_request: true,
    id: 0,
    client,
    table_name,
    FormSchema,
    adjustData: adjustDataForSubmission,
    isNew: true,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New User
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="email"
                    label={t(`${table_name}.columns.email`, {
                      ns: "tables",
                      defaultValue: "Email",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="firstname"
                    label={t(`${table_name}.columns.firstname`, {
                      ns: "tables",
                      defaultValue: "First Name",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="lastname"
                    label={t(`${table_name}.columns.lastname`, {
                      ns: "tables",
                      defaultValue: "Last Name",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="tableau_username"
                    label={t(`${table_name}.columns.tableau_username`, {
                      ns: "tables",
                      defaultValue: "Tableau Username",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="group_user_name"
                    label={t(`${table_name}.columns.group_user_name`, {
                      ns: "tables",
                      defaultValue: "Group User",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="category"
                    label={t(`${table_name}.columns.category`, {
                      ns: "tables",
                      defaultValue: "Category",
                    })}
                    options={categoryOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="role"
                    label={t(`${table_name}.columns.role`, {
                      ns: "tables",
                      defaultValue: "Role",
                    })}
                    options={roleOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                {/* Row 4 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="status"
                    label={t(`${table_name}.columns.status`, {
                      ns: "tables",
                      defaultValue: "Status",
                    })}
                    options={statusOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="type"
                    label={t(`${table_name}.columns.type`, {
                      ns: "tables",
                      defaultValue: "Type",
                    })}
                    options={typeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields
              ? Diff(changedFields, ["group_user_id"])
              : "No changes detected"}
          </ConfirmationModal>

          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
